// externals
import _defaults from 'lodash.defaults';

// libraries
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import type {
    OfferType,
    PaymentSchedule,
    PaymentTerm,
    ResidualInfo,
    ResidualValueType,
    TermStrategy
} from '@makemydeal/dr-platform-types';
import {
    CASH,
    FINANCE,
    LEASE,
    OFFER_TYPE,
    RESIDUAL_DOLLAR,
    RESIDUAL_PERCENT,
    SpecialPaymentFormula
} from '@makemydeal/dr-platform-types';

// interfaces/types
import type { AppliedProgramInfo, PaymentErrorCategory, TProgramInfoAll } from '../types/paymentTypes';
import type { Dealer } from '../types/commonTypes';

// utils
import { isPaymentInvalid } from '@makemydeal/dr-platform-shared';
import type { OfferTermOffer } from '../types/offerTypes';
import {
    getCurrentOfferType,
    getHonorOverrides,
    getIncompleteDealDetails,
    getIsUserProgramQuotes,
    getMonthlyPayment,
    getMonthlyPaymentByOfferType,
    getOfferDetails,
    getOfferDetailsMonth,
    getSelectedTerm,
    getSelectedTermByOfferType,
    getTermByOfferTypeAndMonths,
    getTermsByOfferType
} from './offerNodeSelectors';
import { getCapCostReduction } from './offerSelectors';
import { getRateType, getSellRate } from './selectedTermSelectors';
import { getOfferNode } from './helpers';

const INCLUDE_TAXES_AND_STATE_FEES = 'includeTaxesAndStateFees';
const INCLUDE_ONLY_TAXES = 'includeOnlyTaxes';
const INCLUDE_TAX_OPTIONS = [INCLUDE_TAXES_AND_STATE_FEES, INCLUDE_ONLY_TAXES];

const getState = (state: any) => {
    if (state['@@dr_payment']) {
        return state;
    }
    if (state.payment) {
        return state.payment;
    }
    return {};
};

export const getPaymentState = (state: OfferReduxBaseState): any => {
    return getState(state);
};

export function isCalculatingPayment(state: OfferReduxBaseState): boolean {
    return getState(state).isCalculating ?? false;
}

export function isAwaitingFreshPaymentResponse(state: OfferReduxBaseState): boolean {
    return getState(state).isAwaitingFreshPaymentResponse ?? false;
}

export function isFetchingPayment(state: OfferReduxBaseState): boolean {
    return getState(state).isFetching ?? false;
}

export function getQuestions(state: OfferReduxBaseState) {
    return getState(state).questionsList || [];
}

export function getLocationIdQuestion(state: OfferReduxBaseState) {
    return getQuestions(state).filter((question: any) => {
        return question.id === 'LocationID';
    })[0];
}

export function getPaymentLocationId(state: OfferReduxBaseState) {
    return getState(state).locationId;
}

export function getZipCode(state: Record<string, any>) {
    return getState(state).zipCode || '';
}

export function getLastSuccessfulZipCode(state: OfferReduxBaseState) {
    return getState(state).lastSuccessfulZipCode || '';
}

export function getLastFailedZipCode(state: OfferReduxBaseState) {
    return getState(state).lastFailedZipCode || '';
}

export function getIsError(state: OfferReduxBaseState): boolean {
    return getState(state).error ?? false;
}

export function getErrorMessages(state: OfferReduxBaseState): OfferReduxBaseState[] {
    return getState(state).errorMessages ?? [];
}

/**
 * Why the last payment call failed. `NO_VALID_QUOTES` means no payment came back;
 * `UNIFI_TIMEOUT` and `UNIFI_ERROR` mean the call itself failed.
 *
 * Only written on failure. A later success clears it, but a failure that doesn't write it (e.g. a zip
 * failure) leaves the previous value standing — so pair this with `getIsError`.
 */
export function getErrorCategory(state: OfferReduxBaseState): PaymentErrorCategory | undefined {
    return getState(state).errorCategory;
}

export function getIsShopperUpdateFailed(state: OfferReduxBaseState) {
    return getState(state).shopperUpdateFailed || false;
}

export function getLastPaymentMissingUPQFields(state: OfferReduxBaseState): boolean {
    return getState(state).lastPaymentMissingUPQFields || false;
}

export function getIsLenderChanged(state: OfferReduxBaseState) {
    return getState(state).isLenderChanged || false;
}

export function getTraceId(state: OfferReduxBaseState) {
    return getState(state).traceId || '';
}

export function getActionForPayment(state: OfferReduxBaseState) {
    return getState(state).actionForPayment || '';
}

export function shouldIncludeTaxes(state: Record<string, any>) {
    return getState(state).includeTaxes || false;
}

export function shouldIncludeGovernmentFees(state: OfferReduxBaseState) {
    return getState(state).includeGovernmentFees;
}

export function getSelectedTermsByOfferType(state: OfferReduxBaseState, offerType: OfferType) {
    const dealerCreditTiers = state.dealer.creditTiers;
    const creditTierOfferNode = offerType === 'finance' ? dealerCreditTiers.finance : dealerCreditTiers.lease;
    const selectedCreditTierId = state.creditScore.high;
    const selectedCreditTier = creditTierOfferNode.find((creditTier: any) => creditTier.id === selectedCreditTierId);
    return selectedCreditTier.terms || [];
}

export const getDealerZip = (dealer: any) => (dealer && dealer.address.zip) || '';

export const getDealerApplyTaxesAndFeesNewOptions = (dealer: any) => {
    return (dealer && dealer.applyTaxesAndFeesNewOptions) || INCLUDE_TAXES_AND_STATE_FEES;
};

export const getIncludeGovernmentFees = (dealer: any) => {
    const dealerTaxAndFeeOption = getDealerApplyTaxesAndFeesNewOptions(dealer);
    return dealerTaxAndFeeOption === INCLUDE_TAXES_AND_STATE_FEES;
};

export const getCalculationSettings = (calculationDetails: any) => {
    const {
        useCalculatedGovFees,
        useDealerDefaultDealerBasedTaxes,
        useDealerDefaultDealerFees,
        useDealerDefaultGovFees,
        useDealerDefaultLenderFees,
        useDealerDefaultTaxes
    } = calculationDetails?.payment?.calculationSettings || {};

    return {
        useDealerDefaultLenderFees,
        useDealerDefaultDealerFees,
        useDealerDefaultGovFees,
        useDealerDefaultTaxes,
        useDealerDefaultDealerBasedTaxes,
        useCalculatedGovFees
    };
};

export const isTaxesIncluded = (dealer: Dealer) => {
    const dealerTaxAndFeeOption = getDealerApplyTaxesAndFeesNewOptions(dealer);

    return INCLUDE_TAX_OPTIONS.includes(dealerTaxAndFeeOption);
};

export const getExcludeStdCashIncs = (dealer: any, financeType: any) => {
    if (!dealer) {
        return false;
    } else {
        switch (financeType) {
            case 'Loan':
                return dealer.excludeStdCashIncsLoan || false;
            case 'Lease':
                return dealer.excludeStdCashIncsLease || false;
            case 'Cash':
                return dealer.excludeStdCashIncsCash || false;
            default:
                return false;
        }
    }
};

export const getExcludeStdCashIncsByOfferType = (dealer: any, offerType: OfferType) => {
    if (!dealer) {
        return false;
    } else {
        switch (offerType) {
            case FINANCE:
                return dealer.excludeStdCashIncsLoan || false;
            case LEASE:
                return dealer.excludeStdCashIncsLease || false;
            case CASH:
                return dealer.excludeStdCashIncsCash || false;
            default:
                return false;
        }
    }
};

export const getExcludeSpecialAprsByOfferType = (dealer: any, offerType: OfferType) => {
    if (dealer) {
        switch (offerType) {
            case FINANCE:
                return dealer.excludeSpecialAprsLoan || false;
            case LEASE:
                return dealer.excludeSpecialAprsLease || false;
            default:
                return false;
        }
    } else {
        return false;
    }
};

export const getShouldIncludeLease = (dealer: any) => (dealer && dealer.shouldIncludeLease) || false;

export const getIsNewVehicleSinglePrice = (dealer: any) => dealer.isNewVehicleSinglePrice || false;

export const getIsUsedVehicleSinglePrice = (dealer: any) => dealer.isUsedVehicleSinglePrice || false;

// for leasing cap cost dealer toggle. Default needs to be true
export const getIsDueAtSigninAmountDeduct = (dealer: any) => dealer.leaseSettings.isDueAtSigninAmountDeduct;

export const getAllowMultipleSecurityDepositPayments = (dealer: any) => {
    return Boolean(dealer?.leaseSettings.allowMultipleSecurityDepositPayments);
};

export const shouldUseGenericCalcs = (state: OfferReduxBaseState, hostSelectors: any, paymentOfferType?: OfferType): boolean => {
    const isHonorOverridesInSV = getHonorOverrides(state);

    if (!isHonorOverridesInSV && !hostSelectors?.isManagerView) {
        return false;
    }
    // Use the payment's offer type so a Finance-UPQ deal doesn't force the Alt Deal Structure lease grid to UPQ.
    return getIsUserProgramQuotes(state, paymentOfferType);
};

export const getShopperState = (state: OfferReduxBaseState): string | null => {
    const locationId = getPaymentLocationId(state);

    return locationId ? locationId.split('-')[0] : null;
};

export const getPaymentProgramInfo = (state: OfferReduxBaseState): TProgramInfoAll => {
    const payment = getState(state);
    return payment.programInfoByTerm || {};
};

export const isUpqLockKeptTerm = (state: OfferReduxBaseState, offerType: OfferType, months?: number): boolean =>
    months != null && getState(state).upqLockKeptTerms?.[offerType]?.[months] === true;

export const getProgramEndDays = (state: OfferReduxBaseState): number => {
    const payment = getState(state);
    return payment.programEndDays;
};

export const getPaymentProgramInfoByTerm = (
    state: OfferReduxBaseState,
    offerType?: OfferType,
    months?: number
): AppliedProgramInfo | undefined => {
    const type = offerType || getCurrentOfferType(state);
    const term = months || getOfferDetailsMonth(state, type);

    const paymentInfo = getPaymentProgramInfo(state);
    return paymentInfo?.[type as keyof typeof paymentInfo]?.[term!];
};

export const getTermStrategy = (state: OfferReduxBaseState): TermStrategy => {
    const payment = getState(state);
    return payment.termStrategy;
};

export function isPaymentSuccessfullyCalculatedForOfferType(state: OfferReduxBaseState) {
    const offerType = getCurrentOfferType(state);
    return getPaymentStatusForOfferType(state, offerType);
}

/** It will return a boolean with the status for the payment based on offer type */
export function getPaymentStatusForOfferType(state: OfferReduxBaseState, offerType: OfferType) {
    const termsForOfferType = getTermsByOfferType(state, offerType);
    return !!termsForOfferType?.length;
}

export function isValidMonthlyPayment(state: OfferReduxBaseState) {
    const monthlyPayment = getMonthlyPayment(state, getCurrentOfferType(state));
    return !!(monthlyPayment && monthlyPayment > 0);
}

export const hasMissingPaymentData = (state: OfferReduxBaseState) => {
    const incompleteDealDetails = getIncompleteDealDetails(state);
    return incompleteDealDetails?.missingPaymentData || false;
};

export const getMoneyFactor = (state: OfferReduxBaseState): number => {
    if (getCurrentOfferType(state) === OFFER_TYPE.lease && getRateType(state) === 'MoneyFactor') {
        return getSellRate(state) || 0;
    }

    return 0;
};

export const getResidualInfoOverride = (state: OfferReduxBaseState, enableFallback = false): ResidualInfo => {
    const isUserProgramQuotes = getIsUserProgramQuotes(state, LEASE);

    if (isUserProgramQuotes) {
        const leaseInputs: any = getOfferDetails(state, LEASE);
        const {
            adjustedResidualAmountOverride,
            adjustedResidualPercentOverride,
            adjustedResidualTypeOverride,
            baseResidualAmountOverride,
            baseResidualPercentOverride,
            baseResidualTypeOverride,
            programResidualPercent,
            programResidual
        } = leaseInputs;
        return {
            adjustedResidualAmount: adjustedResidualAmountOverride,
            adjustedResidualPercent: adjustedResidualPercentOverride,
            adjustedResidualType: adjustedResidualTypeOverride,
            baseResidualAmount: baseResidualAmountOverride,
            baseResidualPercent: baseResidualPercentOverride,
            baseResidualType: baseResidualTypeOverride,
            programResidualPercent: programResidualPercent,
            programResidual: programResidual
        };
    } else {
        const termDetails = getSelectedTermByOfferType(state, LEASE);
        const months = termDetails?.months || getOfferDetailsMonth(state, LEASE);
        return months
            ? (getResidualInfoOverrideByOfferTypeTerm(state, LEASE, months, enableFallback) as ResidualInfo)
            : ({} as ResidualInfo);
    }
};

/** True when a lease has no residual at all — every residual field is empty (or the object is absent). */
export const isLeaseResidualMissing = (
    residual?: Partial<
        Pick<ResidualInfo, 'baseResidualAmount' | 'baseResidualPercent' | 'adjustedResidualAmount' | 'adjustedResidualPercent'>
    > | null
): boolean =>
    !residual ||
    (residual.baseResidualAmount == null &&
        residual.baseResidualPercent == null &&
        residual.adjustedResidualAmount == null &&
        residual.adjustedResidualPercent == null);

/**
 * Get specific residual info by offertype and term
 * @param state
 * @param offerType OfferType to retrieve
 * @param months Specific term to retrieve residual info
 * @returns A specific residual info by term and offer type.
 */
export const getResidualInfoOverrideByOfferTypeTerm = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    months: number,
    enableFallback?: boolean
): ResidualInfo | undefined => {
    // The main idea here is to "fill in" missing values from the defaults on this term while prioritizing penciled inputs.
    // This eliminates our problematic penciled inputs initialization.

    // because this function is months based; we can't just get the "selected term"
    const termDefaults = getTermByOfferTypeAndMonths(state, offerType, months);
    const defaultValues = {
        adjustedResidualAmount: termDefaults?.adjustedResidualValue,
        adjustedResidualPercent: termDefaults?.adjustedResidualPercent,
        adjustedResidualType: offerType == LEASE ? RESIDUAL_PERCENT : RESIDUAL_DOLLAR,
        baseResidualAmount: termDefaults?.baseResidualDollar,
        baseResidualPercent: termDefaults?.baseResidualPercent,
        programResidualPercent: termDefaults?.programResidualPercent,
        programResidual: termDefaults?.programResidual,
        baseResidualType: offerType == LEASE ? RESIDUAL_PERCENT : RESIDUAL_DOLLAR
    };

    const penciledInputs = getOfferDetails(state, offerType);

    const residualInfoOverrides = penciledInputs.overridesByTerm?.[months]?.residualInfo;

    if (enableFallback) {
        const result = _defaults({}, residualInfoOverrides, defaultValues);
        return result;
    }
    return residualInfoOverrides;
};

export const getBaseResidualValue = (state: OfferReduxBaseState): number => {
    const residualInfo = getResidualInfoOverride(state, true);
    return residualInfo.baseResidualAmount;
};

export const getSelectedCapCostReduction = (state: OfferReduxBaseState) => {
    const offerType = getCurrentOfferType(state);
    return getCapCostReduction(state, offerType);
};

export function isMonthlyPaymentInvalidByOfferType(state: OfferReduxBaseState, offerType: OfferType) {
    return isPaymentInvalid(getMonthlyPaymentByOfferType(state, offerType), offerType);
}

export const getFirstPaymentDateBySelectedTerm = (state: OfferReduxBaseState): string | undefined => {
    const offerType = getCurrentOfferType(state);
    const termDetails = getSelectedTerm(state, offerType);
    return termDetails?.firstPaymentDate;
};

export const getDaysToFirstPaymentBySelectedTerm = (state: OfferReduxBaseState): number | undefined => {
    const offerType = getCurrentOfferType(state);
    const termDetails = getSelectedTerm(state, offerType);
    return termDetails?.daysToFirstPayment;
};

export const getContractDate = (state: OfferReduxBaseState) => {
    return getState(state).contractDate;
};

export const getPaymentsCalculated = (state: OfferReduxBaseState): boolean => {
    return getState(state).paymentsCalculated === 'true';
};

export const getPaymentSpecialFormula = (offer: OfferTermOffer): SpecialPaymentFormula => {
    const paymentTerm = offer.payment;
    return paymentTerm?.specialPaymentFormula;
};

export const isFordFlexBuyPayment = (offer: OfferTermOffer): boolean => {
    return getPaymentSpecialFormula(offer) === SpecialPaymentFormula.FordFlexBuy;
};

export const getPaymentTermId = (state: OfferReduxBaseState): string | undefined => {
    const offer = getOfferNode(state);
    const payment = getPaymentState(offer);
    return payment?.id;
};

export const getLastPaymentFrequency = (state: OfferReduxBaseState): PaymentSchedule | undefined => {
    const offer = getOfferNode(state);
    const payment = getPaymentState(offer);
    return payment?.paymentFrequency;
};

export const getDealPaymentProgram = (state: OfferReduxBaseState): Partial<PaymentTerm> | undefined => {
    const payment = getPaymentState(getOfferNode(state));
    if (!payment?.lenderProgram) return undefined;
    const {
        lenderProgram,
        lenderLegalName,
        lenderCode,
        lenderName,
        branchName,
        programLenderName,
        creditTier,
        creditTierDescription,
        lenderAddress,
        lenderPhoneNumber
    } = payment;
    return {
        lenderProgram,
        lenderLegalName,
        lenderCode,
        lenderName,
        branchName,
        programLenderName,
        creditTier,
        creditTierDescription,
        lenderAddress,
        lenderPhoneNumber
    };
};

export const getResidualTypeFromCalculationDetails = (
    calculationDetails: { payment?: { calculationOverrides?: { residualType?: string } } } | undefined
): ResidualValueType | undefined => {
    const residualType = calculationDetails?.payment?.calculationOverrides?.residualType;
    if (residualType == null) return undefined;

    const normalizedResidualType = String(residualType).toLowerCase();
    if (normalizedResidualType === RESIDUAL_PERCENT) return RESIDUAL_PERCENT;
    if (normalizedResidualType === RESIDUAL_DOLLAR) return RESIDUAL_DOLLAR;
    return undefined;
};
